<script setup>
import IndexNavbar from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import {onMounted, ref, computed} from "vue";
import {useRoute, useRouter} from 'vue-router'
import Plotly from 'plotly.js-dist'
import {ElLoading, ElMessage} from 'element-plus'
import * as d3 from "d3";

const showExpressionPanels = ref(false)
import {watch, nextTick} from 'vue'

// 监听面板显示状态
watch(showExpressionPanels, async (visible) => {
  if (visible) {
    await nextTick()
    // 等 DOM 展开后强制 Plotly 重新布局
    const umap = document.getElementById('geneUmapPlot')
    const box = document.getElementById('boxPlot')
    if (umap) Plotly.Plots.resize(umap)
    if (box) Plotly.Plots.resize(box)
  }
})

async function drawUmapAnnotation(tissue) {
  const loadingInstance = ElLoading.service({
    fullscreen: true,
    text: 'Loading',
    background: 'rgba(0, 0, 0, 0.7)',
    lock: true
  })
  try {
    const res = await $axios.get(`http://bioinfo-sysu.com:8282/api/get_umap_annotation?tissue=${tissue}`);
    const data = res.data;

    // ✅ 根据 tissue 动态决定分类字段
    const categoryKey = tissue === 'Blood' ? 'sub_celltype' : 'major_celltype';
    const categories = [...new Set(data.map(d => d[categoryKey]))];

    const colors = [
      '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728',
      '#9467bd', '#8c564b', '#e377c2', '#7f7f7f',
      '#bcbd22', '#17becf', '#8dd3c7', '#ffffb3',
      '#bebada', '#fb8072', '#80b1d3', '#fdb462'
    ];

    // ✅ 按选择的分类字段绘制 trace
    const traces = categories.map((type, i) => {
      const subset = data.filter(d => d[categoryKey] === type);
      return {
        x: subset.map(d => d.UMAP_1),
        y: subset.map(d => d.UMAP_2),
        text: subset.map(
          d =>
            `Cell: ${d.cell_id}<br>Major: ${d.major_celltype}<br>Sub: ${d.sub_celltype}`
        ),
        mode: 'markers',
        type: 'scattergl',
        name: type,
        marker: {
          size: 5,
          color: colors[i % colors.length],
          opacity: 0.8
        },
        hoverinfo: 'text'
      };
    });

    const layout = {
      title: {
        text: `UMAP — ${tissue} (${categoryKey === 'sub_celltype' ? 'Sub Cell Annotation' : 'Cell Annotation'})`,
        font: { size: 20 }
      },
      xaxis: { title: 'UMAP-1' },
      yaxis: { title: 'UMAP-2' },
      hovermode: 'closest',
      showlegend: true,
      legend: {
        title: { text: categoryKey === 'sub_celltype' ? 'Sub Cell Type' : 'Major Cell Type' },
        font: { size: 12 }
      },
      margin: { t: 60, r: 100, l: 60, b: 60 }
    };

    const config = {
      responsive: true,
      displaylogo: false
    };

    Plotly.newPlot('umapPlot', traces, layout, config);
  } catch (err) {
    console.error('❌ Failed to load UMAP annotation data:', err);
  }
  loadingInstance.close()
}



async function drawUmapExpression(tissue, gene) {
  const loadingInstance = ElLoading.service({
    fullscreen: true,
    text: 'Loading',
    background: 'rgba(0, 0, 0, 0.7)',
    lock: true
  })
  if (gene == '') {
    gene='CODG18169'
  }
  const res = await $axios.get(`http://bioinfo-sysu.com:8282/api/get_umap_expression?tissue=${tissue}&gene=${gene}`)
  const data = res.data

  const trace = {
    x: data.map(d => d.UMAP_1),
    y: data.map(d => d.UMAP_2),
    text: data.map(d =>
        `Cell: ${d.cell_id}<br>Expr: ${d.expr.toFixed(2)}`
    ),
    mode: 'markers',
    type: 'scattergl',
    marker: {
      size: 5,
      color: data.map(d => d.expr),
      colorscale: 'Viridis',
      colorbar: {title: 'Expression'}
    }
  }

  const layout = {
    title: `UMAP — ${tissue} (${gene} Expression)`,
    xaxis: {title: 'UMAP-1'},
    yaxis: {title: 'UMAP-2'},
    hovermode: 'closest'
  }

  Plotly.newPlot('geneUmapPlot', [trace], layout)

  // ----------------------
  // 小提琴图绘制（加宽版）
  // ----------------------
  const processed = data.map(d => ({
    major_simple: d.major_celltype.split('_')[0],
    expr: d.expr
  }))

  const majors = [...new Set(processed.map(d => d.major_simple))]

  const violinTraces = majors.map(m => {
    const values = processed.filter(d => d.major_simple === m).map(d => d.expr)
    return {
      y: values,
      name: m,
      type: 'violin',
      width: 0.8,                        // ✅ 每列宽度加大（默认约 0.5，可调 0.5~1.5）
      box: {visible: true},
      meanline: {visible: true},
      points: 'outliers',
      jitter: 0.5,
      scalemode: 'width',
      opacity: 0.8,
      marker: {size: 3},
      line: {width: 1}
    }
  })

  const violinLayout = {
    title: `Expression Distribution of ${gene} across Major Cell Types`,
    font: {family: 'Arial, sans-serif', size: 16},
    yaxis: {
      title: 'Expression Level',
      titlefont: {size: 16},
      tickfont: {size: 14},
      zeroline: false
    },
    xaxis: {
      title: '',
      tickangle: -45,
      tickfont: {size: 14},
      automargin: true
    },
    violinmode: 'group',
    violingap: 0.02,         // ✅ 减小间距，让提琴更密集
    violingroupgap: 0.02,
    margin: {t: 80, b: 150},
    showlegend: false
  }

  const config = {
    responsive: true,
    displaylogo: false,
    toImageButtonOptions: {format: 'png', filename: `ViolinPlot_${tissue}_${gene}`}
  }

  Plotly.newPlot('boxPlot', violinTraces, violinLayout, config)


  loadingInstance.close()
}

async function handleSearch() {
  if (!inputText.value) {
    inputText.value='CODG18169'
  }
  // 绘制两张图
  await drawUmapExpression(tissue.value, inputText.value)
  showExpressionPanels.value = true   // ✅ 搜索后才显示两个折叠框
}


// 添加窗口大小变化监听，确保图表始终填满容器
window.addEventListener('resize', function () {
  if (typeof drawBoxPlot === 'function') {
    setTimeout(drawBoxPlot, 200);
  }
});


import {onBeforeRouteUpdate, onBeforeRouteLeave} from 'vue-router'

let route = useRoute()
let router = useRouter()
// 检查tissue参数是否存在
if (!route.query.tissue) {
  router.replace({
    path: '/',
  })
}

// 防止用户在当前页面删除参数
onBeforeRouteUpdate((to, from, next) => {
  if (!to.query.tissue) {
    // 保留其他参数，添加tissue参数
    router.replace({
      ...to,
      query: {
        ...to.query,
        tissue: from.query.tissue || 'default'
      }
    })
  } else {
    next()
  }
})

// 可选：添加路由离开守卫
onBeforeRouteLeave((to, from, next) => {
  // 可以在这里添加离开前的逻辑
  // 例如保存数据或确认离开
  next()
})
//axios请求
import {inject} from 'vue'
import {ArrowRight} from '@element-plus/icons-vue'

const activePanels = ref(['umap', 'Plot', 'Violin'])
onMounted(() => {
  const query = route.query
  if (query.tissue) {
    tissue.value = query.tissue
    drawUmapAnnotation(tissue.value)
  }
});
const tissue = ref('')
//gene搜索
const inputText = ref('')

function inputInput(e) {
  inputText.value = e.target.value;
}

const $axios = inject('$axios')


// const downloadFile = (fileName) => {
//   // 文件路径（假设文件名为data.csv）
//   const filePath = './file/' +fileName// 文件位于public目录下
//
//   // 创建隐藏的可下载链接
//   const link = document.createElement('a')
//   link.href = filePath
//   link.download = fileName // 设置下载文件名
//
//   // 添加到DOM并触发点击
//   document.body.appendChild(link)
//   link.click()
//
//   // 清理DOM
//   document.body.removeChild(link)
// };
</script>
<template>
  <div>
    <index-navbar/>
    <section class="relative w-full h-full py-40 min-h-screen">
      <div class="container mx-auto items-center flex flex-wrap">
        <div class="col-12 my-3">
          <el-breadcrumb :separator-icon="ArrowRight" style="font-size: 22px">
            <el-breadcrumb-item :to="{ path: '/' }"><i class="bi bi-house me-1"></i>Home</el-breadcrumb-item>
            <el-breadcrumb-item :to="{ path: '/singlecellinfo' }">Single Cell</el-breadcrumb-item>
            <el-breadcrumb-item>{{ tissue }}</el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        <div class="col-12">
          <div class="py-2 border-t border-blueGray-200 text-center">
            <div class="flex flex-wrap justify-center">
              <div class="w-full lg:w-9/12 px-4">
                <p class="text-lg leading-relaxed text-blueGray-700">
                </p>
              </div>
            </div>
          </div>
        </div>
        <!--        <div class="col-md-6">-->
        <!--          <img :src="UMAP_celltype">-->
        <!--        </div>-->
        <!--        <div class="col-md-6">-->
        <!--          <img :src="edit_FeaturePlot">-->
        <!--        </div>-->
        <div class="col-12">
          <div class="d-flex flex-row justify-content-center align-items-center mb-8">
                  <span>Select Gene:
                  </span>
            <input type="text" placeholder="CODG18169" @input="inputInput" :value="inputText"
                   class="mx-3 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 relative bg-white bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline"/>
            <el-tooltip
                placement="top-start"
            >
              <template #content> e.g., 'CODG18169'
              </template>
              <i class="bi bi-question-circle"></i>
            </el-tooltip>
            <button
                class="bg-lightBlue-500 text-white active:bg-lightBlue-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 mx-3"
                type="button"
                @click="handleSearch">
              Search
            </button>
          </div>
          <div class="gene-dashboard">
            <!-- Gene Information 折叠面板 -->
            <el-collapse v-model="activePanels" class="dashboard-collapse my-3">
              <el-collapse-item name="umap">
                <template #title>
                  <div class="panel-header">
                    <i class="el-icon-document"></i>
                    <span>Annotation</span>
                  </div>
                </template>
                <div class="panel-content">
                  <div id="umapPlot" style="width: 100%; height: 700px"></div>
                </div>
              </el-collapse-item>
            </el-collapse>
            <el-collapse v-show="showExpressionPanels" v-model="activePanels" class="dashboard-collapse my-3">
              <el-collapse-item name="Plot">
                <template #title>
                  <div class="panel-header">
                    <i class="el-icon-data-analysis"></i>
                    <span>Gene Expression (Plot)</span>
                  </div>
                </template>
                <div class="panel-content">
                  <div class="expression-header">
                    <div id="geneUmapPlot" style="width: 100%; height: 700px"></div>
                  </div>
                </div>
              </el-collapse-item>
            </el-collapse>
            <el-collapse v-show="showExpressionPanels" v-model="activePanels" class="dashboard-collapse my-3">
              <el-collapse-item name="Violin">
                <template #title>
                  <div class="panel-header">
                    <i class="el-icon-document"></i>
                    <span>Gene Expression (Violin)</span>
                  </div>
                </template>
                <div class="panel-content">
                  <div id="boxPlot" style="width: 100%; height: 700px"></div>
                </div>
              </el-collapse-item>
            </el-collapse>
          </div>
        </div>
      </div>

    </section>

    <footer-component/>
  </div>
</template>
<style scoped>
.gene-dashboard {
  max-width: 100%;
  margin: 20px auto;
}

.dashboard-collapse {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.dashboard-collapse :deep(.el-collapse-item) {
  border-bottom: 1px solid #e0e0e0;
}

.dashboard-collapse :deep(.el-collapse-item):last-child {
  border-bottom: none;
}

.dashboard-collapse :deep(.el-collapse-item__header) {
  background-color: #f8f9fa;
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  border: none;
}

.dashboard-collapse :deep(.el-collapse-item__header):hover {
  background-color: #e9ecef;
}

.dashboard-collapse :deep(.el-collapse-item__wrap) {
  background-color: #fff;
  border: none;
}

.dashboard-collapse :deep(.el-collapse-item__content) {
  padding: 0;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.panel-header i {
  color: #409eff;
  font-size: 18px;
}

.panel-content {
  padding: 20px;
}

.gene-info-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.gene-info-table th,
.gene-info-table td {
  padding: 12px 15px;
  text-align: left;
  border: 1px solid #e0e0e0;
}

.gene-info-table th {
  background-color: #f5f7fa;
  font-weight: 600;
  color: #303133;
}

.gene-info-table tbody tr:nth-child(even) {
  background-color: #f9f9f9;
}

.expression-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.expression-header h3 {
  margin: 0;
  color: #303133;
  font-weight: 600;
}


</style>